import type { JSX } from "react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useLiveQuery } from "dexie-react-hooks";
import type { CookSessionApiItem, RecipeApiItem } from "@recipevault/shared";
import { db } from "../../sync/db.js";
import { setFocusedSessionId } from "./focusedSession.js";
import { progressOf } from "./progress.js";
import { useOpenCookSessions } from "./useOpenCookSessions.js";
import { WaitingSessionSheet } from "./WaitingSessionSheet.js";
import { Card, CardHeader, CardTitle, CardContent } from "../../ui/card.js";
import { Badge } from "../../ui/badge.js";

// ---------------------------------------------------------------------------
// ActiveCookRow — one row per open session, loads recipe via useLiveQuery
// ---------------------------------------------------------------------------

interface ActiveCookRowProps {
  session: CookSessionApiItem;
  onSelectWaiting: (session: CookSessionApiItem) => void;
}

function ActiveCookRow({ session, onSelectWaiting }: ActiveCookRowProps): JSX.Element {
  const navigate = useNavigate();
  const recipe = useLiveQuery<RecipeApiItem | undefined>(
    () => db.recipes.get(session.recipeId),
    [session.recipeId],
  );

  const progress =
    recipe && recipe.steps.length > 0
      ? progressOf(recipe.steps, session.stepStates, session.currentStepId)
      : null;

  function handleClick(): void {
    if (session.status === "active" || session.status === "paused") {
      setFocusedSessionId(session.id);
      void navigate("/cook/" + session.id);
    } else if (session.status === "waiting") {
      onSelectWaiting(session);
    }
  }

  const statusLabel =
    session.status === "active"
      ? "Active"
      : session.status === "paused"
        ? "Paused"
        : "Waiting";

  const statusVariant =
    session.status === "active"
      ? ("default" as const)
      : session.status === "paused"
        ? ("secondary" as const)
        : ("outline" as const);

  const progressLabel =
    progress != null ? `Step ${progress.currentIndex} of ${progress.total}` : statusLabel;

  return (
    <button
      type="button"
      data-testid={`session-row-${session.id}`}
      onClick={handleClick}
      className="w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-zinc-800/60 transition-colors"
    >
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-sm font-medium text-zinc-100 truncate">
          {recipe?.title ?? "Loading…"}
        </span>
        <span className="text-xs text-zinc-400">{progressLabel}</span>
      </div>
      <Badge variant={statusVariant}>{statusLabel}</Badge>
    </button>
  );
}

// ---------------------------------------------------------------------------
// ActiveCooksCard
// ---------------------------------------------------------------------------

export function ActiveCooksCard(): JSX.Element | null {
  const sessions = useOpenCookSessions();
  const [waitingSession, setWaitingSession] = useState<CookSessionApiItem | null>(null);

  // Look up the recipe title for the waiting sheet
  const waitingRecipe = useLiveQuery<RecipeApiItem | undefined>(
    () =>
      waitingSession ? db.recipes.get(waitingSession.recipeId) : Promise.resolve(undefined),
    [waitingSession?.recipeId],
  );

  if (sessions.length === 0) return null;

  return (
    <>
      <Card className="py-4 gap-2">
        <CardHeader className="px-4 pb-0">
          <CardTitle className="text-sm font-semibold text-zinc-300 uppercase tracking-wide">
            Currently cooking
          </CardTitle>
        </CardHeader>
        <CardContent className="px-2 pb-0">
          {sessions.map((s) => (
            <ActiveCookRow
              key={s.id}
              session={s}
              onSelectWaiting={setWaitingSession}
            />
          ))}
        </CardContent>
      </Card>

      <WaitingSessionSheet
        session={waitingSession}
        recipeTitle={waitingRecipe?.title}
        open={waitingSession !== null}
        onOpenChange={(open) => {
          if (!open) setWaitingSession(null);
        }}
      />
    </>
  );
}
